import React, { useEffect, useRef, useState } from 'react';

import { Stack } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';

import { AccessResourceKey } from 'hooks/data/files/types';
import useReleaseVersions from 'hooks/data/versions/useReleaseVersions';
import useAccess from 'hooks/useAccess';
import useAuthUser from 'hooks/useAuthUser';
import useTourAvailable from 'hooks/useTourAvailable';
import useVersionInfo from 'hooks/useVersionInfo';
import { checkAccessToResource, checkAccessToV4 } from 'utilities/auth/access';
import { setMixpanelLicenseProperties } from 'utilities/auth/user';

import AnnouncementBanner from '../AnnouncementBanner';
import ConsentBanner from '../ConsentBanner';
import LicensedTour, { useLicensedTour } from '../LicensedTour';
import { TOUR_DATASET_ID, TOUR_VERSION } from '../LicensedTour/constants';
import QuestionnaireCard from '../Questionnaire/QuestionnaireCard';
import QuestionnaireOverlay from '../Questionnaire/QuestionnaireOverlay';
import SideBar from '../SideBar';
import MobileSideBar from '../SideBar/MobileSideBar';
import UnlicensedTour, { useUnlicensedTour } from '../UnlicensedTour';

type Props = {
  children?: React.ReactNode;
};

const Layout: React.FC<Props> = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth();
  // v5's isLoading turns false while a retry is paused (hidden tab, offline); v3's stayed true, so these
  // gates use `isPending && fetchStatus !== 'idle'` to avoid acting on data that is still on its way.
  const { data: authUser, isPending: isAuthPending, fetchStatus: authFetchStatus } = useAuthUser();
  const isAuthLoading = isAuthPending && authFetchStatus !== 'idle';
  const { state: licensedTourState, startTour: startLicensedTour } = useLicensedTour();
  const { state: unlicensedTourState, startTour: startUnlicensedTour } = useUnlicensedTour();
  const { data: accessData, isPending: isAccessPending, fetchStatus: accessFetchStatus } = useAccess();
  const isAccessLoading = isAccessPending && accessFetchStatus !== 'idle';

  const navigate = useNavigate();
  const hasTriggeredLicensedTour = useRef(false);
  const hasTriggeredUnlicensedTour = useRef(false);

  // Track cookie consent state reactively
  const [hasCookieConsent, setHasCookieConsent] = useState(() => {
    if (!window.Cookiebot) return true;
    if (window.Cookiebot.loaded && window.Cookiebot.dialog === null) return true;
    return window.Cookiebot.hasResponse;
  });

  // Add User Properties for user licenses
  useEffect(() => {
    if (!authUser) return;
    setMixpanelLicenseProperties(authUser.license_info);
  }, [authUser]);

  // Listen for Cookiebot consent events
  useEffect(() => {
    // Sync with current Cookiebot state in case it responded between mount and effect
    if (window.Cookiebot?.hasResponse || (window.Cookiebot?.loaded && window.Cookiebot?.dialog === null)) {
      setHasCookieConsent(true);
    }

    const handleCookieConsent = () => setHasCookieConsent(true);

    window.addEventListener('CookiebotOnAccept', handleCookieConsent);
    window.addEventListener('CookiebotOnDecline', handleCookieConsent);

    return () => {
      window.removeEventListener('CookiebotOnAccept', handleCookieConsent);
      window.removeEventListener('CookiebotOnDecline', handleCookieConsent);
    };
  }, []);

  const { ver, system_model } = useVersionInfo();

  useEffect(() => {
    mixpanel.register({ version: ver, system_model });
  }, [ver, system_model]);

  const { data: versions, isPending: areVersionsPending, fetchStatus: versionsFetchStatus } = useReleaseVersions();
  const areVersionsLoading = areVersionsPending && versionsFetchStatus !== 'idle';

  const isTourAvailable = useTourAvailable();
  const hasV4Access = checkAccessToV4(versions);
  const hasTourVersionAccess = checkAccessToResource(accessData, AccessResourceKey.SCORES, TOUR_VERSION);
  const hasTourConfig = !!TOUR_VERSION && !!TOUR_DATASET_ID;

  // Access check is complete when:
  // - User is not authenticated (no access data to fetch), or
  // - User is authenticated and access data has finished loading
  const isAccessCheckComplete = !isAuthenticated || !isAccessLoading;

  // Blocking questionnaire for first-time users
  const [questionnaireJustCompleted, setQuestionnaireJustCompleted] = useState(false);
  const showBlockingQuestionnaire =
    !isLoading &&
    !isAuthLoading &&
    isAuthenticated &&
    !!authUser &&
    authUser.first_session_completed !== true &&
    !questionnaireJustCompleted;

  useEffect(() => {
    if (!areVersionsLoading && !hasV4Access && ver.includes('ei25')) {
      navigate('/404');
    }
  }, [hasV4Access, navigate, ver, areVersionsLoading]);

  // Trigger onboarding tour based on user's licence status
  useEffect(() => {
    const canTriggerTour =
      !isLoading &&
      !isAuthLoading &&
      isAccessCheckComplete &&
      hasTourConfig &&
      hasCookieConsent &&
      isTourAvailable &&
      !showBlockingQuestionnaire;

    if (!canTriggerTour) return;

    const shouldTriggerLicensedTour =
      isAuthenticated &&
      authUser &&
      hasTourVersionAccess &&
      !licensedTourState.hasCompletedTour &&
      authUser.tour_completed !== true &&
      !licensedTourState.isActive &&
      !hasTriggeredLicensedTour.current;

    if (shouldTriggerLicensedTour) {
      hasTriggeredLicensedTour.current = true;
      setTimeout(() => {
        startLicensedTour('auto');
      }, 400);
      return;
    }

    const shouldTriggerUnlicensedTour =
      !hasTourVersionAccess &&
      !unlicensedTourState.hasCompletedTour &&
      authUser?.tour_completed !== true &&
      !unlicensedTourState.isActive &&
      !licensedTourState.isActive &&
      !hasTriggeredUnlicensedTour.current;

    if (shouldTriggerUnlicensedTour) {
      hasTriggeredUnlicensedTour.current = true;
      setTimeout(() => {
        startUnlicensedTour('auto');
      }, 400);
    }
  }, [
    isLoading,
    isAuthLoading,
    isAccessCheckComplete,
    isAuthenticated,
    authUser,
    hasTourConfig,
    hasTourVersionAccess,
    hasCookieConsent,
    licensedTourState.hasCompletedTour,
    licensedTourState.isActive,
    unlicensedTourState.hasCompletedTour,
    unlicensedTourState.isActive,
    isTourAvailable,
    showBlockingQuestionnaire,
    startLicensedTour,
    startUnlicensedTour,
  ]);

  const shouldShowConsentBanner = !isLoading && !isAuthLoading && isAuthenticated && !authUser?.agreement_accepted;

  if (showBlockingQuestionnaire) {
    return (
      <QuestionnaireOverlay
        isOpen
        onComplete={() => setQuestionnaireJustCompleted(true)}
        trigger="first_login"
        allowSkip={false}
      />
    );
  }

  return (
    <Stack width={'100%'} spacing={0}>
      <LicensedTour />
      <UnlicensedTour />
      <QuestionnaireCard />
      <MobileSideBar />
      <Stack direction="row" spacing={0} alignItems={'stretch'} height={{ md: '100vh' }}>
        <SideBar />
        <Stack maxW="100%" w="100%" overflowY="auto">
          <AnnouncementBanner />
          {shouldShowConsentBanner && <ConsentBanner />}
          <Stack
            py={{ base: 0, md: 3 }}
            px={{ base: 0, md: 5 }}
            maxW="100%"
            w="100%"
            flex={1}
            margin={'0 auto'}
            alignItems="center"
          >
            {children}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Layout;
